import {Component, ElementRef, Inject, OnInit, ViewEncapsulation} from '@angular/core';
import {AbstractControl, FormControl, FormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {User} from '../../gen/model/user';
import {AppConstants} from '../AppConstants';
import {StandaloneComponent} from '../standalone.component';
import {AlertHandler} from '../no-concurrent-alerts-handler';
import {cardFooterFeedbackTransition} from '../animations';
import {
  WebAuthnPublicKeyCredential,
  WebAuthnService
} from '../../gen';
import {Observable} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {ConfigurationService} from '../ui-configuration/configuration-service';
import {AppStateService} from '../app-state.service';
import {startRegistration} from '@simplewebauthn/browser';
import {formatDate} from '@angular/common';


@Component({
  selector: 'app-configure-passkey',
  templateUrl: './configure-passkey.component.html',
  styleUrls: ['./configure-passkey.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    cardFooterFeedbackTransition
  ],
})
export class ConfigurePasskeyComponent extends StandaloneComponent implements OnInit {

  passkeyIsActive: boolean;

  passKeyForm: FormGroup;
  credentialIds: string[]|undefined;
  passkeys: any[]|undefined;
  passkeyCreds: Omit<WebAuthnPublicKeyCredential, 'authenticationMethod'>|undefined;
  passkeyRegisterActive: boolean|string;
  passkeyError: any;
  profile: User|undefined;
  constructor(private appStateService: AppStateService,
              private alertHandler: AlertHandler,
              @Inject('WebAuthnApi') private webAuthnApi: WebAuthnService,
              private translate: TranslateService,
              route: ActivatedRoute,
              router: Router,
              hostElement: ElementRef,
              configuration: ConfigurationService) {
    super(route, router, hostElement, configuration);
  }
  getProfile(): User {
    return this.appStateService.getAppState().getAuthenticationState().getProfile();
  }
  ngOnInit() {
    super.init();
    this.onProfileLoaded(this.getProfile());
    this.loadCredentials();
    const t = this.appStateService.getAppState().getAuthenticationState().getExistingAuthentications();
    if (t && t.length) {
      const auth = t.find((f) => f.authenticationMethod === AppConstants.AC_AM_WEB_AUTHN_PUBLIC_KEY);
      if (!!auth && !!(auth as any).credentials) {
        this.credentialIds = ((auth as any).credentials as { id: string }[]).map((m) => m.id);
      } else {
        this.credentialIds = undefined;
      }
    } else {
      this.credentialIds = undefined;
    }

  }
  isUsedAsAuthentication(id: string) {
    return !!this.credentialIds?.find((f) => f === id);
  }
  loadCredentials() {
    this.webAuthnApi.listWebAuthnCredentials('response', false).subscribe((response) => {
      if (response && response.body && response.body.length) {
        this.passkeyIsActive = true;
        this.passkeys = response.body;
      } else {
        this.passkeyIsActive = false;
        this.passkeys = [];
      }
      const controls = {};
      this.passkeys.forEach((m, index) => {
        controls['pk_' + index] = new FormControl(m.name, []);
      });
      this.passKeyForm = new FormGroup(controls);
    });
  }

  onProfileLoaded(user: User) {
    this.profile = user;
  }

  startPasskeyRegister() {
    this.passkeyError = undefined;
    this.passkeyRegisterActive = true;
    const format = 'longDate';
    const locale = this.translate.currentLang || this.translate.defaultLang;
    const displayName = this.profile.preferredUsername || (!!this.profile.firstName || !!this.profile.lastName ?
      [this.profile.firstName, this.profile.lastName].filter((f) => !!f).join(' ') :
        this.profile.email
    );
    this.webAuthnApi.startCredentialRegistration({
      name: this.profile.email,
      userDisplayName: displayName,
      credentialNickname: formatDate(new Date(), format, locale), // used for identifying the key when there are multiple.
    }).subscribe(async (resp) => {
      this.passkeyRegisterActive = resp.requestId;
      let attResp;
      try {
        const options = JSON.parse(resp.publicKeyCredentialCreationOptions);
        // Pass the options to the authenticator and wait for a response
        attResp = await startRegistration(options);
        this.passkeyCreds = {
          publicKeyCredential: JSON.stringify(attResp),
          requestId: resp.requestId,
        };
        this.webAuthnApi.completeCredentialRegistration({
          ...this.passkeyCreds,
          messageKey: 'passkeyActivated'
        }).subscribe((complResp) => {
          this.passkeyRegisterActive = false;
          this.loadCredentials();
        }, (err) => {
          this.passkeyError = err;
        });
      } catch (error) {
        this.passkeyError = error;
        if (this.passkeyRegisterActive === resp.requestId) {
          this.passkeyRegisterActive = false;
        }
        if (error.name === 'NotAllowedError') {
          // manually cancelled, or timed out
          this.passkeyError = undefined;
        } else if (error.name === 'InvalidStateError') {
          // Probably already registered
        }
        this.passkeyCreds = undefined;
      }
    }, (err) => {
      this.passkeyError = err;
    });
  }
  deletePasskey(id: string) {
    this.webAuthnApi.deleteWebAuthnCredentials({
      messageKey: 'passkeyDeactivated'
    }, id).subscribe((delResp) => {
      this.loadCredentials();
    }, (err) => {

    });
  }
  signOut() {
    this.router.navigate([AppConstants.PATH_LOGOUT], {
      relativeTo: this.route,
    });
  }

  hasError(field?: AbstractControl, code?: string): boolean {
    return !!this.passkeyError;
  }

  public resolveWindowTitlePart(): Observable<string|undefined> {
    return this.translate.get('configure-passkey.window-title');
  }

}
